import React, { useState, useEffect } from 'react'
import './userdashboardtwofactor.css'
import { useNavigate } from 'react-router-dom'
import { FiShield, FiCheckCircle, FiCopy } from 'react-icons/fi'
import Swal from 'sweetalert2'
import Loader from '../Loader'
import Userdashboardheader from '../userdashboardheader/Userdashboardheader'
import DashboardTopbar from '../dashboardShell/DashboardTopbar'
import PageHero from '../dashboardShell/PageHero'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
})

const UserdashboardTwoFactor = ({ route }) => {
  const navigate = useNavigate()
  const [loader, setLoader] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [setup, setSetup] = useState(null) // { qrCode, secret } while enrolling
  const [disabling, setDisabling] = useState(false)
  const [code, setCode] = useState('')

  useEffect(() => {
    const getData = async () => {
      try {
        setLoader(true)
        const token = localStorage.getItem('token')
        if (!token) { navigate('/login'); return }
        const data = await (await fetch(`${route}/api/getData`, {
          headers: { 'x-access-token': token, 'Content-Type': 'application/json' }
        })).json()
        if (data.status === 'error') { localStorage.removeItem('token'); navigate('/login') }
        else setEnabled(Boolean(data.twoFactorEnabled))
      } catch { navigate('/login') }
      finally { setLoader(false) }
    }
    getData()
  }, [navigate, route])

  const post = async (path, body = {}) => {
    const response = await fetch(`${route}${path}`, {
      method: 'POST',
      headers: { 'x-access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return response.json()
  }

  const startSetup = async () => {
    try {
      setLoader(true)
      const result = await post('/api/2fa/setup')
      if (result.status === 200) setSetup({ qrCode: result.qrCode, secret: result.secret })
      else Toast.fire({ icon: 'warning', title: result.message })
    } catch {
      Toast.fire({ icon: 'error', title: 'Something went wrong. Please try again.' })
    } finally {
      setLoader(false)
    }
  }

  const submitCode = async (e) => {
    e.preventDefault()
    try {
      setLoader(true)
      const result = await post(enabled ? '/api/2fa/disable' : '/api/2fa/enable', { code })
      if (result.status !== 200) {
        Toast.fire({ icon: 'warning', title: result.message })
        return
      }
      Toast.fire({ icon: 'success', title: result.message })
      setEnabled(!enabled)
      setSetup(null)
      setDisabling(false)
      setCode('')
    } catch {
      Toast.fire({ icon: 'error', title: 'Something went wrong. Please try again.' })
    } finally {
      setLoader(false)
    }
  }

  const cancel = () => {
    setSetup(null)
    setDisabling(false)
    setCode('')
  }

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setup.secret)
      Toast.fire({ icon: 'success', title: 'Key copied' })
    } catch {
      Toast.fire({ icon: 'warning', title: 'Could not copy, please copy it manually' })
    }
  }

  const codeForm = (buttonLabel, buttonClass) => (
    <form className="udtf-code-form" onSubmit={submitCode}>
      <div className="ud-form-field">
        <label htmlFor="udtf-code">Authentication code</label>
        <input
          id="udtf-code"
          className="ud-form-input udtf-code-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="123456"
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      <div className="udtf-actions">
        <button type="button" className="ud-btn-ghost" onClick={cancel}>Cancel</button>
        <button type="submit" className={buttonClass} disabled={code.length !== 6}>{buttonLabel}</button>
      </div>
    </form>
  )

  return (
    <div className="ud-layout">
      {loader && <Loader />}
      <Userdashboardheader route={route} />

      <div className="ud-main">
        <DashboardTopbar route={route} title="Two-Factor Authentication" />

        <div className="ud-content narrow">
          <PageHero
            icon={<FiShield />}
            eyebrow="Security"
            title="Two-factor authentication"
            description="Add a second step to your sign-in using an authenticator app such as Google Authenticator or Authy."
          />

          <div className="udtf-wrap">

            <div className="ud-card">
              <div className="udtf-status-row">
                <div className={`udtf-status-icon${enabled ? ' on' : ''}`}>
                  {enabled ? <FiCheckCircle size={22} /> : <FiShield size={22} />}
                </div>
                <div className="udtf-status-text">
                  <h3>{enabled ? '2FA is on' : '2FA is off'}</h3>
                  <p>
                    {enabled
                      ? 'You will be asked for a code from your authenticator app every time you sign in.'
                      : 'Your account is protected by your password only. Turn on 2FA to keep it safe even if your password is compromised.'}
                  </p>
                </div>
                <span className={`ud-badge ${enabled ? 'ud-badge-profit' : 'ud-badge-pending'}`}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {!enabled && !setup && (
                <button className="ud-btn-primary udtf-main-btn" onClick={startSetup}>Set up 2FA</button>
              )}

              {!enabled && setup && (
                <div className="udtf-setup">
                  <ol className="udtf-steps">
                    <li>Install an authenticator app on your phone.</li>
                    <li>Scan this QR code with the app, or enter the key manually.</li>
                    <li>Enter the 6-digit code the app shows to confirm.</li>
                  </ol>
                  <div className="udtf-qr-row">
                    <img src={setup.qrCode} alt="2FA QR code" className="udtf-qr" />
                    <div className="udtf-secret">
                      <span className="udtf-secret-label">Setup key</span>
                      <code>{setup.secret}</code>
                      <button type="button" className="ud-btn-ghost udtf-copy-btn" onClick={copySecret}>
                        <FiCopy size={14} /> Copy key
                      </button>
                    </div>
                  </div>
                  {codeForm('Enable 2FA', 'ud-btn-primary')}
                </div>
              )}

              {enabled && !disabling && (
                <button className="ud-btn-danger udtf-main-btn" onClick={() => setDisabling(true)}>Turn off 2FA</button>
              )}

              {enabled && disabling && (
                <div className="udtf-setup">
                  <p className="udtf-hint">Enter a current code from your authenticator app to turn off 2FA.</p>
                  {codeForm('Turn off 2FA', 'ud-btn-danger')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserdashboardTwoFactor
